import { useMemo, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useKDSStore } from '../stores/kdsStore';
import { useKitchenPolling } from '../hooks/useKitchenPolling';
import { preshiftConfigApi } from '../services/api';
import StationSelector from '../components/StationSelector';
import TicketGrid from '../components/TicketGrid';
import EightySixModal from '../components/EightySixModal';
import {
  Settings,
  Ban,
  RefreshCw,
  History,
  Volume2,
  VolumeX,
  ChefHat,
  Wifi,
  WifiOff,
  Bell,
  Megaphone,
  X,
  AlertTriangle,
  Layers,
} from 'lucide-react';
import clsx from 'clsx';

interface Announcement {
  id: string;
  title: string;
  message: string;
  priority: 'urgent' | 'high' | 'normal' | 'low';
  createdAt: string;
}

export default function KDSPage() {
  const navigate = useNavigate();
  const { tickets, settings, updateSettings, getAvailableCourses } = useKDSStore();
  const { isLoading, refetch, isRealtime, error: ticketsError, lastUpdatedAt } = useKitchenPolling();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showAnnouncements, setShowAnnouncements] = useState(false);
  const [show86Board, setShow86Board] = useState(false);
  const [dismissedAnnouncements, setDismissedAnnouncements] = useState<Set<string>>(() => {
    const stored = localStorage.getItem('kds_dismissedAnnouncements');
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  // Fetch announcements
  const { data: announcementsData } = useQuery({
    queryKey: ['kds-announcements'],
    queryFn: async () => {
      const response = await preshiftConfigApi.getAnnouncements('cook');
      if (response.data?.success && response.data?.data) {
        return response.data.data as Announcement[];
      }
      return [];
    },
    refetchInterval: 60000, // Check every minute
    staleTime: 30000,
  });

  // Filter out dismissed announcements
  const activeAnnouncements = (announcementsData || []).filter(
    (a: Announcement) => !dismissedAnnouncements.has(a.id)
  );

  // Urgent announcements render as a strip above the tickets (see below) —
  // never an auto-opening modal. The old effect reopened the modal on every
  // render (activeAnnouncements is a fresh array each time), so cooks had to
  // dismiss-and-lose a message just to see their tickets.
  const urgentStrip = activeAnnouncements.filter(
    (a: Announcement) => a.priority === 'urgent' || a.priority === 'high'
  );

  const dismissAnnouncement = useCallback((id: string) => {
    setDismissedAnnouncements(prev => {
      const updated = new Set(prev);
      updated.add(id);
      localStorage.setItem('kds_dismissedAnnouncements', JSON.stringify([...updated]));
      return updated;
    });
  }, []);

  const dismissAllAnnouncements = useCallback(() => {
    setDismissedAnnouncements(prev => {
      const updated = new Set(prev);
      activeAnnouncements.forEach((a: Announcement) => updated.add(a.id));
      localStorage.setItem('kds_dismissedAnnouncements', JSON.stringify([...updated]));
      return updated;
    });
    setShowAnnouncements(false);
  }, [activeAnnouncements]);

  // Update clock every second
  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Calculate ticket counts per station
  const ticketCounts = useMemo(() => {
    const counts: Record<string, number> = { all: 0 };

    tickets.forEach((ticket) => {
      if (ticket.status === 'completed') return;

      counts.all++;
      ticket.items.forEach((item) => {
        if (item.status !== 'completed') {
          counts[item.station] = (counts[item.station] || 0) + 1;
        }
      });
    });

    return counts;
  }, [tickets]);

  // Stats for header
  const stats = useMemo(() => {
    const activeTickets = tickets.filter((t) => t.status !== 'completed');
    const inProgressTickets = activeTickets.filter(
      (t) => t.status === 'in_progress'
    );
    const readyTickets = activeTickets.filter((t) => t.status === 'ready');

    return {
      active: activeTickets.length,
      inProgress: inProgressTickets.length,
      ready: readyTickets.length,
    };
  }, [tickets]);

  // Available courses for filtering
  const availableCourses = getAvailableCourses();

  const setCourseFilter = (course: number | null) => {
    updateSettings({ selectedCourse: course });
  };

  const toggleSound = () => {
    updateSettings({ alertSoundEnabled: !settings.alertSoundEnabled });
  };

  return (
    <div className="h-[100dvh] flex flex-col bg-slate-900 overflow-hidden">
      {/* Header - with safe area */}
      <header className="bg-slate-800 border-b border-slate-700 px-4 py-3 flex flex-wrap items-center justify-between gap-y-2 safe-top flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3">
            <ChefHat className="w-8 h-8 text-primary-400" />
            <img src="/mise-wordmark.svg" alt="mise" className="h-5 invert hidden sm:block" />
            {/* Kitchens run on the clock — keep it visible at every size */}
            <div className="flex items-center bg-slate-700/50 rounded-full px-2.5 py-1">
              <p className="text-xs text-slate-300 tabular-nums">
                {currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>

          {/* Stats - visible on larger screens */}
          <div className="hidden lg:flex items-center gap-4 ml-6">
            <div className="bg-slate-700/50 rounded-lg px-3 py-1.5">
              <span className="text-slate-400 text-xs">Active</span>
              <span className="ml-2 font-bold text-white text-lg">{stats.active}</span>
            </div>
            <div className="bg-blue-900/50 rounded-lg px-3 py-1.5">
              <span className="text-blue-400 text-xs">Cooking</span>
              <span className="ml-2 font-bold text-blue-300 text-lg">{stats.inProgress}</span>
            </div>
            <div className="bg-emerald-900/50 rounded-lg px-3 py-1.5">
              <span className="text-emerald-400 text-xs">Ready</span>
              <span className="ml-2 font-bold text-emerald-300 text-lg">{stats.ready}</span>
            </div>
          </div>
        </div>

        {/* Mobile stats — labeled, not a bare number */}
        <div className="flex lg:hidden items-center gap-2 text-sm">
          <span className="bg-slate-700 rounded-full px-2.5 py-1 font-bold">{stats.active} open</span>
          {stats.ready > 0 && (
            <span className="bg-emerald-600 rounded-full px-2.5 py-1 font-bold animate-pulse">
              {stats.ready} ready
            </span>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Connection indicator */}
          <div className={clsx(
            'p-2 rounded-lg',
            isRealtime ? 'text-emerald-400' : 'text-slate-500'
          )}>
            {isRealtime ? <Wifi className="w-5 h-5" /> : <WifiOff className="w-5 h-5" />}
          </div>

          <button
            onClick={toggleSound}
            className={clsx(
              'p-3 rounded-xl transition-colors btn-press',
              settings.alertSoundEnabled
                ? 'bg-primary-600 text-white'
                : 'bg-slate-700 text-slate-400'
            )}
          >
            {settings.alertSoundEnabled ? (
              <Volume2 className="w-5 h-5" />
            ) : (
              <VolumeX className="w-5 h-5" />
            )}
          </button>

          <button
            onClick={() => refetch()}
            disabled={isLoading}
            className="p-3 rounded-xl bg-slate-700 text-slate-300 active:bg-slate-600 transition-colors btn-press"
          >
            <RefreshCw
              className={clsx('w-5 h-5', isLoading && 'animate-spin')}
            />
          </button>

          {/* Announcements Button */}
          {activeAnnouncements.length > 0 && (
            <button
              onClick={() => setShowAnnouncements(true)}
              className="p-3 rounded-xl bg-amber-600 text-white active:bg-amber-700 transition-colors btn-press relative"
              title="View Announcements"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-xs font-bold flex items-center justify-center">
                {activeAnnouncements.length}
              </span>
            </button>
          )}

          <button
            onClick={() => setShow86Board(true)}
            className="p-3 rounded-xl bg-slate-700 text-red-300 active:bg-slate-600 transition-colors btn-press"
            title="86 board"
          >
            <Ban className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate('/history')}
            className="p-3 rounded-xl bg-slate-700 text-slate-300 active:bg-slate-600 transition-colors btn-press"
            title="History / recall"
          >
            <History className="w-5 h-5" />
          </button>

          <button
            onClick={() => navigate('/settings')}
            className="p-3 rounded-xl bg-slate-700 text-slate-300 active:bg-slate-600 transition-colors btn-press"
          >
            <Settings className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Station Selector */}
      <div className="px-2 sm:px-4 py-2 bg-slate-800/50 border-b border-slate-700 flex-shrink-0">
        <StationSelector ticketCounts={ticketCounts} />
      </div>

      {/* Course Filter Tabs */}
      {availableCourses.length > 0 && (
        <div className="px-2 sm:px-4 py-2 bg-slate-800/30 border-b border-slate-700/50 flex-shrink-0">
          <div className="flex items-center gap-2 overflow-x-auto">
            <Layers className="w-4 h-4 text-slate-500 flex-shrink-0" />
            <span className="text-xs text-slate-500 flex-shrink-0">Course:</span>
            <button
              onClick={() => setCourseFilter(null)}
              className={clsx(
                'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0',
                settings.selectedCourse === null
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              )}
            >
              All
            </button>
            {availableCourses.map((course) => (
              <button
                key={course}
                onClick={() => setCourseFilter(course)}
                className={clsx(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors flex-shrink-0',
                  settings.selectedCourse === course
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                )}
              >
                Course {course}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Connection trouble — kitchens must KNOW when tickets may be stale */}
      {ticketsError != null && (
        <div className="bg-amber-500 text-white text-center text-sm font-medium py-1.5 px-4 flex-shrink-0">
          Reconnecting — showing tickets from{' '}
          {lastUpdatedAt
            ? `${Math.max(1, Math.round((currentTime.getTime() - lastUpdatedAt) / 60000))} min ago`
            : 'a moment ago'}
          . New orders will appear when the connection returns.
        </div>
      )}

      {/* Ticket Grid */}
      <TicketGrid currentTime={currentTime} />

      {/* Footer - minimal on mobile */}
      <footer className="bg-slate-800 border-t border-slate-700 px-4 py-2 safe-bottom flex-shrink-0">
        <div className="flex items-center justify-between text-xs text-slate-500">
          <div className="flex items-center gap-2">
            {isRealtime ? (
              <span className="text-emerald-400">Live</span>
            ) : (
              <span>Polling every 10s</span>
            )}
          </div>
          <div className="hidden sm:flex items-center gap-4">
            <span>
              Warning: {settings.warningThresholdMinutes}m | Critical: {settings.criticalThresholdMinutes}m
            </span>
          </div>
          <div className="text-slate-400 font-medium">
            {currentTime.toLocaleTimeString()}
          </div>
        </div>
      </footer>

      {show86Board && <EightySixModal onClose={() => setShow86Board(false)} />}

      {/* Announcements Modal */}
      {urgentStrip.length > 0 && (
        <div className="bg-amber-500 text-white px-4 py-2 flex items-center gap-3 shrink-0 cursor-pointer" onClick={() => setShowAnnouncements(true)}>
          <span className="font-semibold text-sm uppercase tracking-wide shrink-0">{urgentStrip[0].priority === 'urgent' ? 'Urgent' : 'Heads up'}</span>
          <span className="text-sm truncate flex-1">{urgentStrip[0].title ? `${urgentStrip[0].title} — ` : ''}{urgentStrip[0].message || ''}</span>
          <span className="text-xs opacity-80 shrink-0">{urgentStrip.length > 1 ? `+${urgentStrip.length - 1} more · ` : ''}tap to read</span>
        </div>
      )}
      {showAnnouncements && activeAnnouncements.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-start justify-center z-50 p-4 pt-20">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md shadow-xl max-h-[70vh] flex flex-col border border-slate-700">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-amber-400" />
                <h3 className="text-lg font-bold text-white">Kitchen Announcements</h3>
              </div>
              <button
                onClick={() => setShowAnnouncements(false)}
                className="p-1 hover:bg-slate-700 rounded-lg"
              >
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="flex-1 overflow-auto p-4 space-y-3">
              {activeAnnouncements.map((announcement: Announcement) => {
                const priorityColors: Record<string, string> = {
                  urgent: 'bg-red-900/50 border-red-500 text-red-200',
                  high: 'bg-orange-900/50 border-orange-500 text-orange-200',
                  normal: 'bg-blue-900/50 border-blue-500 text-blue-200',
                  low: 'bg-slate-700/50 border-slate-500 text-slate-300',
                };
                const colorClass = priorityColors[announcement.priority] || priorityColors.normal;

                return (
                  <div
                    key={announcement.id}
                    className={clsx('rounded-xl p-4 border-2 relative', colorClass)}
                  >
                    <button
                      onClick={() => dismissAnnouncement(announcement.id)}
                      className="absolute top-2 right-2 p-1 hover:bg-white/10 rounded"
                      title="Dismiss"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div className="pr-6">
                      <div className="flex items-center gap-2 mb-1">
                        {(announcement.priority === 'urgent' || announcement.priority === 'high') && (
                          <AlertTriangle className="w-4 h-4" />
                        )}
                        <p className="font-semibold">{announcement.title}</p>
                      </div>
                      <p className="text-sm mt-1 opacity-90">{announcement.message}</p>
                      <p className="text-xs mt-2 opacity-70">
                        {new Date(announcement.createdAt).toLocaleString([], {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="p-4 border-t border-slate-700">
              <button
                onClick={dismissAllAnnouncements}
                className="w-full py-3 bg-slate-700 text-slate-300 rounded-xl font-semibold hover:bg-slate-600 transition-colors"
              >
                Dismiss All
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
